import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ExamProctor from "../utils/examProctor";
import "./ExamShellContext.css";

/*
 * ExamShellContext — React port of the original portal.html "shell" plus
 * exam-lock.js.
 *
 * In the original static-HTML project, portal.html was the single top-level
 * browser tab. It requested fullscreen ONCE and then loaded every other page
 * (login.html -> instructions.html -> dashboard.html -> summary.html) inside
 * an <iframe>, so navigating between pages never left fullscreen. Every framed
 * page included exam-lock.js, which detected it was running inside that shell
 * (via window.parent.__EXAM_SHELL__) and asked the shell to (re)enforce the
 * lock, guard keys, and trap browser history.
 *
 * Converting the project to a single-page React app removes the need for the
 * iframe trick entirely: React Router already renders every "page" inside the
 * same top-level document, so the fullscreen session is naturally preserved
 * across navigations. This context reproduces the shell's exact behaviour
 * (start gate, resume gate, key guard, history trap, keyboard lock) without
 * the iframe, and every exam page consumes it instead of talking to
 * `window.parent`.
 */

const LOCK_KEY = "examStatus";

const ExamShellCtx = createContext(null);

function isFs() {
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.msFullscreenElement
  );
}

export function ExamShellProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [started, setStarted] = useState(false);
  const [released, setReleased] = useState(false);
  const [fullscreen, setFullscreen] = useState(isFs());
  const [showResumeGate, setShowResumeGate] = useState(false);

  // Remember which route the candidate actually wanted, the same way
  // portal.html read "?p=" from the query string.
  const intendedPathRef = useRef(location.pathname === "/" ? "/login" : location.pathname);

  const lockedRef = useRef(false);
  lockedRef.current = !released && started;

  /* ---- Keyboard Lock: makes Escape a normal key inside fullscreen ---- */
  const lockKeys = useCallback(() => {
    try {
      if (navigator.keyboard && navigator.keyboard.lock) {
        const r = navigator.keyboard.lock(["Escape", "F11"]);
        if (r && r.catch) r.catch(() => {});
      }
    } catch (e) {
      /* ignore */
    }
  }, []);

  const unlockKeys = useCallback(() => {
    try {
      if (navigator.keyboard && navigator.keyboard.unlock) navigator.keyboard.unlock();
    } catch (e) {
      /* ignore */
    }
  }, []);

  const enterFs = useCallback(() => {
    const el = document.documentElement;
    try {
      const r =
        (el.requestFullscreen && el.requestFullscreen({ navigationUI: "hide" })) ||
        (el.webkitRequestFullscreen && el.webkitRequestFullscreen()) ||
        (el.msRequestFullscreen && el.msRequestFullscreen());
      if (r && r.then) r.then(lockKeys).catch(() => {});
      else setTimeout(lockKeys, 200);
    } catch (e) {
      /* ignore */
    }
  }, [lockKeys]);

  const exitFs = useCallback(() => {
    const fn = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
    if (fn && isFs()) {
      try {
        const r = fn.call(document);
        if (r && r.catch) r.catch(() => {});
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  /* ---- Public API mirroring the old window.ExamShell / window.ExamLock ---- */
  const ensure = useCallback(() => {
    if (released) return;
    setStarted(true);
    if (!isFs()) setShowResumeGate(true);
    else lockKeys();
  }, [released, lockKeys]);

  const release = useCallback(() => {
    setReleased(true);
    setShowResumeGate(false);
    localStorage.removeItem(LOCK_KEY);
    unlockKeys();
    exitFs();
  }, [unlockKeys, exitFs]);

  const handleStart = useCallback(() => {
    setStarted(true);
    enterFs();
    const target = intendedPathRef.current || "/login";
    if (location.pathname === "/") navigate(target, { replace: true });
  }, [enterFs, navigate, location.pathname]);

  const handleResume = useCallback(() => {
    enterFs();
    setShowResumeGate(false);
  }, [enterFs]);

  /* ---- Sync fullscreen state + resume gate visibility ---- */
  useEffect(() => {
    function sync() {
      const fs = isFs();
      setFullscreen(fs);
      if (released || !started) return;
      if (fs) {
        setShowResumeGate(false);
        lockKeys();
      } else {
        setShowResumeGate(true);
      }
    }
    const events = ["fullscreenchange", "webkitfullscreenchange", "msfullscreenchange"];
    events.forEach((evt) => document.addEventListener(evt, sync));
    return () => events.forEach((evt) => document.removeEventListener(evt, sync));
  }, [released, started, lockKeys]);

  /* ---- Trap browser history: block back/forward while locked ---- */
  useEffect(() => {
    function trapHistory() {
      try {
        window.history.pushState({ exam: 1 }, "", window.location.href);
        window.history.pushState({ exam: 2 }, "", window.location.href);
      } catch (e) {
        /* ignore */
      }
    }
    function onPopState() {
      if (lockedRef.current) {
        try {
          window.history.pushState({ exam: 2 }, "", window.location.href);
        } catch (e) {
          /* ignore */
        }
        if (!isFs()) setShowResumeGate(true);
      }
    }
    if (started) trapHistory();
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [started]);

  /* ---- Swallow Escape / F11 / F12 / dev-tools shortcuts while locked ---- */
  useEffect(() => {
    function keyGuard(e) {
      if (released) return;
      const k = e.key;
      const block =
        k === "Escape" ||
        k === "Esc" ||
        k === "F11" ||
        e.keyCode === 27 ||
        e.keyCode === 122 ||
        e.keyCode === 123 || // F12
        (e.altKey && (k === "ArrowLeft" || k === "ArrowRight")) || // back / forward
        (e.altKey && k === "Tab") ||
        (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67)) ||
        (e.ctrlKey &&
          (e.keyCode === 85 ||
            e.keyCode === 82 ||
            e.keyCode === 87 ||
            e.keyCode === 78 ||
            e.keyCode === 84 ||
            e.keyCode === 80)) ||
        (k === "Backspace" && !/^(INPUT|TEXTAREA)$/.test((e.target && e.target.tagName) || ""));
      if (block) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    }
    ["keydown", "keyup", "keypress"].forEach((evt) =>
      window.addEventListener(evt, keyGuard, true)
    );
    function blockContext(e) {
      if (!released) e.preventDefault();
    }
    document.addEventListener("contextmenu", blockContext);

    function onBlur() {
      if (lockedRef.current && !isFs()) setShowResumeGate(true);
    }
    window.addEventListener("blur", onBlur);

    let closeAttempted = false;

    function onBeforeUnload(e) {
      if (lockedRef.current) {
        closeAttempted = true;
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    }
    window.addEventListener("beforeunload", onBeforeUnload);

    // If the candidate clicks the browser's own close "X" (the one that
    // appears when hovering the top edge in fullscreen) — or Ctrl+W,
    // Alt+F4, closing the window any other way — beforeunload fires above
    // and forces the browser's own "Leave site?" prompt (its wording can't
    // be changed; no page can replace it with custom UI, by design). If the
    // candidate cancels that prompt, the window regains focus. That's the
    // one reliable, cross-browser signal that a close was attempted and
    // cancelled, so it's used here to guarantee the resume gate appears —
    // rather than depending solely on blur/fullscreenchange timing, which
    // varies by browser. The resume gate has exactly one control ("Resume
    // Secure Exam"), so this is the only path back into the exam.
    function onFocusReturn() {
      if (closeAttempted) {
        closeAttempted = false;
        if (lockedRef.current) setShowResumeGate(true);
      }
    }
    window.addEventListener("focus", onFocusReturn);

    return () => {
      ["keydown", "keyup", "keypress"].forEach((evt) =>
        window.removeEventListener(evt, keyGuard, true)
      );
      document.removeEventListener("contextmenu", blockContext);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("focus", onFocusReturn);
    };
  }, [released]);

  /*
   * ---- Early warning for the browser's own top-edge chrome reveal ----
   *
   * Moving the mouse to the very top pixel row makes Chrome/Edge float their
   * tab strip (with its own close "X") over the fullscreen page as a native
   * overlay. No page can hide, block, or even see that overlay — it isn't
   * part of the DOM and fires no events, by deliberate browser design (the
   * same reason no site can hide its own tab-close button). What a page CAN
   * observe is the cursor approaching that zone, or leaving the window
   * outright. Neither proves fullscreen was actually exited — clicking that
   * "X" is what does that, and is already caught instantly by the
   * fullscreenchange listener above — so this doesn't show the resume gate
   * (that would falsely tell an unaffected candidate their session ended).
   * Instead it (a) re-arms the keyboard lock in case focus drifted toward
   * the browser chrome, and (b) records the moment in the same proctor log
   * the webcam checks use, so it's visible to whoever reviews that log.
   */
  useEffect(() => {
    if (!started || released) return undefined;

    let warnedTop = false;

    function onMouseMove(e) {
      if (e.clientY <= 3) {
        if (!warnedTop) {
          warnedTop = true;
          ExamProctor.flag("near_top_edge", "Cursor moved to the top edge of the screen");
        }
        lockKeys();
      } else if (e.clientY > 30) {
        warnedTop = false;
      }
    }

    function onWindowMouseLeave(e) {
      // relatedTarget is null when the cursor leaves the browser window
      // itself (as opposed to moving onto another element in the page).
      if (e.relatedTarget === null && e.target === document.documentElement) {
        ExamProctor.flag("cursor_left_window", "Cursor left the browser window");
        lockKeys();
      }
    }

    document.addEventListener("mousemove", onMouseMove);
    document.documentElement.addEventListener("mouseleave", onWindowMouseLeave);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.documentElement.removeEventListener("mouseleave", onWindowMouseLeave);
    };
  }, [started, released, lockKeys]);

  const value = {
    started,
    released,
    isFullscreen: fullscreen,
    showResumeGate,
    ensure,
    release,
    requestFullscreen: enterFs,
  };

  return (
    <ExamShellCtx.Provider value={value}>
      {/* Start Gate — identical markup/copy to portal.html's #startGate */}
      {!started && (
        <div className="exam-shell-gate" id="startGate">
          <div className="exam-shell-gate-lock">&#128274;</div>
          <h1>Secure Examination Mode</h1>
          <p>
            Your exam will run in locked fullscreen mode. Once started, you will stay in
            fullscreen through login, instructions, the exam and your result summary.
            You may return to a normal window only after finishing the summary page.
          </p>
          <button type="button" id="startBtn" onClick={handleStart}>
            Start Secure Exam
          </button>
          <small>Do not press Escape or switch windows during the exam.</small>
        </div>
      )}

      {/* Resume Gate — identical markup/copy to portal.html's #resumeGate */}
      {started && showResumeGate && (
        <div className="exam-shell-gate" id="resumeGate">
          <div className="exam-shell-gate-lock">&#9888;</div>
          <h1>Fullscreen Mode Exited</h1>
          <p>Secure fullscreen was interrupted. Click below to resume — your progress and answers are safe.</p>
          <button type="button" id="resumeBtn" onClick={handleResume}>
            Resume Secure Exam
          </button>
        </div>
      )}

      {children}
    </ExamShellCtx.Provider>
  );
}

export function useExamShell() {
  const ctx = useContext(ExamShellCtx);
  if (!ctx) {
    throw new Error("useExamShell must be used within an ExamShellProvider");
  }
  return ctx;
}